//React
import React, { useState, useEffect, useMemo } from "react";
import { useAlert } from "../../hooks/useAlert";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { useAuth } from "../../hooks/useAuth";
import { capitalizeFirst } from "../../utils";
import { useModels } from "../../hooks/useModels";
//----------------------------------------------------------------------------------------------------------------------

//Componentes
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import Loading from "../../components/FullScreenLoader";
import { useModel } from "../../hooks/useModel";
import PageHeader from "../../components/PageHeader";
import InputRegex from "../../components/InputRegex";
import Divider from "@mui/material/Divider";
import LoadingButton from "@mui/lab/LoadingButton";
import DatePicker from "../../components/DatePickerBeta";
//----------------------------------------------------------------------------------------------------------------------

//Iconos
import SaveIcon from "@mui/icons-material/Save";
import { Add, Edit } from "@mui/icons-material";
import moment from "moment-timezone";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import Swal from "sweetalert2";
import AsyncAutocompleteSearchRework from "../../components/AsyncAutocompleteSearchRework";
import { useCallback } from "react";
import BackButton from "../../components/BackButton";
//----------------------------------------------------------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

function Detail({ match, history, dialog }) {
  const classes = useStyles();
  const { showAlert } = useAlert();
  const id = match.params.id;
  const editing = !!id;
  const [admin] = useLocalStorage("admin", null);
  const { permiso } = useAuth();
  const [idSucursal, setIdSucursal] = useLocalStorage("idSucursal", "");
  const [sucursal, setSucursal] = useState(null);
  const [equipo, setEquipo] = useState(null);
  const propiedadesDefault = {
    folio: "",
    fecha: moment(new Date()).format("yyyy/MM/DD"),
    noEconomico: "",
    equipo: "",
    operador: "",
    despachador: "",
    precio: "",
    importe: "",
    empresa: "",
    litros: 0,
  }
  const [propiedades, setPropiedades] = useState({ ...propiedadesDefault });

  const equipoParamsMemo = useMemo(
    () =>
      editing
        ? {
          pagina: 1,
          limite: 10,
          ordenar: "creado.desc",
          idEquipo: equipo,
        }
        : { pagina: 1, limite: 10, ordenar: "creado.desc" },
    [editing, equipo]
  );

  const sucursalParamsMemo = useMemo(() => (
    editing
      ? { pagina: 1, limite: 10, ordenar: "nombreSucursal.desc", idSucursal: sucursal }
      : { pagina: 1, limite: 10, ordenar: "nombreSucursal.desc" }
  ), [editing, sucursal]);

  const { model, modelLoading, modelError, updateModel, updateModelLoading } =
    useModel({
      name: "diesel-movimientos",
      id,
      redirectOnPost: false,
    });

  const onGuardar = async () => {
    let title = "";
    let body = propiedades;
    let nombreSucursal = `${body.nombreSucursal}`;
    if (editing) {
      body.folio = id;
    } else {
      editing
        ? (title = "¿Deseas Editar este candidato?")
        : (title = "¿Deseas Guardar este movimiento de diesel?");
      const res = await Swal.fire({
        title: title,
        text: "Favor de corroborar sus datos antes de continuar.",
        icon: "info",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Si, Continuar!",
        cancelButtonText: "Cancelar",
      });
      if (res.isConfirmed) {
        showAlert({
          message: "Guardando movimiento de diesel",
          severity: "warning",
        });
        body = { ...body, nombreSucursal: nombreSucursal };
        updateModel(body, true, "dieselMovimientos");
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted && model) {
      setPropiedades(model);
    }
    return () => {
      mounted = false;
      setPropiedades({
        folio: "",
        fecha: moment(new Date()).format("yyyy/MM/DD"),
        noEconomico: "",
        equipo: "",
        operador: "",
        despachador: "",
        precio: "",
        importe: "",
        empresa: "",
        litros: 0,
      });
    };
  }, [model]);

  const setEquipoCallback = useCallback(
    (e, v) => {
      if (v !== "" && v) {
        console.log(propiedades);
        setEquipo(v);
        setPropiedades({
          ...propiedades,
          importe: propiedades.litros * v.precio,
          idEquipo: v.idEquipo,
          modelo: v.modelo,
          noEconomico: v.noEconomico,
          precio: v.precio
        });
      } else {
        setEquipo(null);
      }
    },
    [propiedades]
  );

  const setSucursalCallback = useCallback((e, v) => {
    if (v !== "" && v) {
      setSucursal(v);
      setPropiedades((props) => ({ ...props, idSucursal: v.idSucursal }));
    } else {
      setSucursal(null);
    }
  }, [setSucursal]);

  if (modelLoading) return <Loading />;

  return (
    <>
      <PageHeader
        history={history}
        title={(!editing ? "Nuevo" : "Editar") + " Movimiento de Diesel"}
        links={[
          {
            to: "/dieselMovimientos",
            icon: <ImportExportIcon />,
            label: "Movimiento diesel",
          },
          {
            to: !editing
              ? "/dieselMovimientos/nuevo"
              : "/dieselMovimientos/editar/" + id,
            icon: !editing ? <Add /> : <Edit />,
            label: !editing
              ? "Nuevo"
              : "Editar - " + capitalizeFirst(propiedades?.economico()),
          },
        ]}
        editing={editing}
        dialog={dialog}
      />
      <Grid
        component={Paper}
        container
        className={classes.paper}
        spacing={1}
        style={{ marginTop: 10 }}
      >
        <Grid item xs={12}>
          <strong>{"INFORMACIÓN MOVIMIENTO DEL DIESEL"}</strong>
          <Divider />
        </Grid>

        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <DatePicker
            labelText="Fecha"
            fullWidth
            inputFormat="dd/MM/yyyy"
            value={propiedades.fecha}
            size="small"
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                fecha: moment(e).format("yyyy/MM/DD"),
              }))
            }
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Folio"
            variant="outlined"
            fullWidth
            size="small"
            typeInput="text"
            maxLength={50}
            value={propiedades.folio}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                folio: e.target.value,
              }))
            }
            helperText={modelError.folio}
            error={Boolean(modelError.folio)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <AsyncAutocompleteSearchRework
            label="Equipo"
            name="equipo"
            variant="outlined"
            labelProp="nombreEquipo"
            extraParams={equipoParamsMemo}
            onChange={setEquipoCallback}
            campoError="idEquipo"
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="No. Economico"
            fullWidth
            disabled
            variant="outlined"
            size="small"
            typeInput="text"
            maxLength={50}
            value={propiedades?.noEconomico}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                noEconomico: e.target.value,
              }))
            }
            helperText={modelError.noEconomico}
            error={Boolean(modelError.noEconomico)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Operador"
            fullWidth
            variant="outlined"
            size="small"
            typeInput="text"
            maxLength={50}
            value={propiedades?.operador}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                operador: e.target.value,
              }))
            }
            helperText={modelError.operador}
            error={Boolean(modelError.operador)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            label="Despachador"
            fullWidth
            variant="outlined"
            size="small"
            typeInput="text"
            maxLength={50}
            value={propiedades?.despachador}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                despachador: e.target.value,
              }))
            }
            helperText={modelError.despachador}
            error={Boolean(modelError.despachador)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <InputRegex
            label="Litros"
            fullWidth
            variant="outlined"
            size="small"
            typeInput="decimal"
            value={propiedades?.litros}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                litros: e.target.value,
                importe: (propiedades.precio * e.target.value).toFixed(2),
              }))
            }
            helperText={modelError.litros}
            error={Boolean(modelError.litros)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="standard">
            <InputLabel id="empresaSelect">Empresa</InputLabel>
            <Select
              labelId="empresaSelect"
              id="empresaSelectId"
              value={propiedades?.empresa}
              label="Empresa"
              onChange={(e) =>
                setPropiedades((prevState) => ({
                  ...prevState,
                  ...prevState.propiedades,
                  empresa: e.target.value,
                }))
              }
            >
              <MenuItem value={"BYLSA DRILLING"}>Bylsa Drilling</MenuItem>
              <MenuItem value={"MINERALES DE TARACHI"}>Minerales de Tarachi</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <InputRegex
            label="Precio"
            variant="outlined"
            fullWidth
            disabled
            size="small"
            typeInput="currency"
            value={propiedades?.precio}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                precio: e.target.value,
              }))
            }
            helperText={modelError.precio}
            error={Boolean(modelError.precio)}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <InputRegex
            label="Importe"
            variant="outlined"
            fullWidth
            disabled
            size="small"
            typeInput="currency"
            value={propiedades?.importe}
            onChange={(e) =>
              setPropiedades((prevState) => ({
                ...prevState,
                ...prevState.propiedades,
                importe: e.target.value,
              }))
            }
            helperText={modelError.importe}
            error={Boolean(modelError.importe)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <AsyncAutocompleteSearchRework
            defaultValue={sucursal}
            label="Nombre del proyecto"
            variant="outlined"
            name="sucursal"
            labelProp="nombreSucursal"
            extraParams={sucursalParamsMemo}
            onChange={setSucursalCallback}
            campoError="idSucursal"
          />
        </Grid>

        <Grid item xs={12}>
          <Grid container justifyContent="center">
            <LoadingButton
              disabled={
                modelLoading ||
                  updateModelLoading ||
                  admin ||
                  (permiso && permiso[0]["dieselMovimientos"] & 2) === 2
                  ? false
                  : true
              }
              loading={modelLoading || updateModelLoading}
              variant="contained"
              onClick={onGuardar}
              size="medium"
              startIcon={<SaveIcon />}
              style={{ color: "#fff" }}
            >
              {!editing ? "GUARDAR" : "MODIFICAR"}
            </LoadingButton>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export default Detail;
